import {useFormikContext} from 'formik'
import {useState} from 'react'
import {InlineTable} from 'src/feature/bite/component'
import * as Yup from 'yup'

export default function FinancialDetails({
  loading,
  intl,
  editable,
  setEditable,
}: {
  loading: boolean
  intl: any
  editable: any
  setEditable: any
}) {
  const frequencyOptions = [
    {value: 1, label: 'Weekly'},
    {value: 2, label: 'BiWeekly'},
    {value: 3, label: 'Monthly'},
    {value: 4, label: 'Quarterly'},
    {value: 5, label: 'Annually'},
    {value: 6, label: 'PerSession'},
  ]

  const payoutDate = [
    {value: 1, label: 'Monday'},
    {value: 2, label: 'Tuesday'},
    {value: 3, label: 'Wednesday'},
    {value: 4, label: 'Thursday'},
    {value: 5, label: 'Friday'},
  ]

  const payoutMethod = [
    {value: 1, label: 'Bank Transfer'},
    {value: 2, label: 'Point'},
    {value: 3, label: 'Bank Card'},
  ]

  const currencies = [
    {value: 0, label: 'AED'},
    {value: 1, label: 'AED'},
    {value: 'eur', label: 'EUR'},
    {value: 'usd', label: 'USD'},
  ]

  const column = [
    [
      {
        key: 'financialOfficialName',
        dataIndex: 'financialOfficialName',
        title: 'Official Name',
        required: true,
        maxLength: 100,
        edit: 'input',
        tooltip:
          'The registered name of your business or legal entity used for financial and tax purposes.',
      },
      {
        title: intl.formatMessage({id: 'GLOBAL.BANK_NAME'}),
        key: 'financialBankName',
        dataIndex: 'financialBankName',
        edit: 'input',
        required: true,
        maxLength: 100,
        tooltip: 'The name of the bank where your payout account is held.',
      },
      {
        title: intl.formatMessage({id: 'GLOBAL.TAX_OFFICE'}),
        key: 'financialTaxOffice',
        dataIndex: 'financialTaxOffice',
        edit: 'input',
        maxLength: 100,
        required: true,
        tooltip: 'The tax office where your business is registered.',
      },
      {
        title: 'Payment Frequency',
        key: 'financialPaymentFrequency',
        dataIndex: 'financialPaymentFrequency',
        valueSelect: true,
        selects: frequencyOptions.map((option: any) => ({
          value: option.value,
          text: option.label,
        })),
        tooltip: 'Specifies how often payments will be processed (e.g., weekly, monthly).',
      },
      {
        title: 'Payout Date',
        key: 'financialPayoutDate',
        dataIndex: 'financialPayoutDate',
        render: (value: any) => {
          return <>{payoutDate.find((option) => option.value === value)?.label || ''}</>
        },
        tooltip: 'The date on which payments are processed.',
      },
    ],
    [
      {
        title: intl.formatMessage({id: 'GLOBAL.INVOICE_ADDRESS'}),
        key: 'financialInvoiceAddress',
        dataIndex: 'financialInvoiceAddress',
        edit: 'input',
        required: true,
        maxLength: 150,
        tooltip: 'The address to be used on invoices for billing purposes.',
      },
      {
        title: intl.formatMessage({id: 'GLOBAL.IBAN'}),
        key: 'financialIBAN',
        dataIndex: 'financialIBAN',
        edit: 'input',
        maxLength: 100,
        required: true,
        tooltip: 'Your International Bank Account Number (IBAN) for receiving payments.',
      },
      {
        title: intl.formatMessage({id: 'GLOBAL.TAX_NUMBER'}),
        key: 'financialTaxNumber',
        dataIndex: 'financialTaxNumber',
        edit: 'input',
        maxLength: 100,
        required: true,
        tooltip: 'Your unique tax identification number for financial reporting.',
      },
      {
        title: 'Payout Method',
        key: 'financialPayoutMethod',
        dataIndex: 'financialPayoutMethod',
        valueSelect: true,
        selects: payoutMethod.map((option: any) => ({
          value: option.value,
          text: option.label,
        })),
        tooltip: 'The method for receiving payments (e.g., bank transfer, digital wallet).',
      },
      {
        title: 'Currency',
        key: 'financialCurrency',
        dataIndex: 'financialCurrency',
        valueSelect: true,
        selects: currencies.map((option: any) => ({
          value: option.value,
          text: option.label,
        })),
        tooltip: 'The currency in which you will receive payments.',
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    financialOfficialName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialInvoiceAddress: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialBankName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxOffice: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialIBAN: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxNumber: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
  })

  const {handleSubmit, values, resetForm, initialValues} = useFormikContext() as any

  return (
    <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
      <div className='card-header'>
        <div className='card-title m-0 d-flex justify-content-between w-100'>
          <div>
            <h3 className='fw-bold mb-2 mt-5'>
              {intl.formatMessage({id: 'GLOBAL.FINANCIAL_DETAILS'})}
            </h3>
            <p className='text-muted fw-normal fs-6'>
              Enter your financial and banking information to ensure smooth transactions and
              payouts.
            </p>
          </div>
        </div>
      </div>

      <div className='card-body p-9'>
        <InlineTable
          loading={loading}
          dataSource={initialValues}
          columns={column}
          editable={editable.financialDetails}
          values={values}
        />
      </div>
      <div className='card-footer d-flex w-100 justify-content-end'>
        {editable.financialDetails ? (
          <div>
            <button
              type='button'
              className='btn btn-primary'
              style={{marginRight: 10}}
              onClick={(e) => {
                e.preventDefault()
                setEditable({
                  overview: false,
                  professionalDetails: false,
                  serviceDetails: false,
                  socialMedia: false,
                  contractDetails: false,
                  financialDetails: false,
                })
                resetForm()
              }}
            >
              <i className='bi bi-x me-2'></i>
              {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={loading}
              onClick={async (e) => {
                values.type = 5
                await handleSubmit()
              }}
            >
              <i className='bi bi-save2 me-2'></i>
              {loading
                ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
            </button>
          </div>
        ) : (
          <button
            className='btn btn-primary'
            onClick={() => {
              resetForm()
              setEditable({
                overview: false,
                professionalDetails: false,
                serviceDetails: false,
                socialMedia: false,
                contractDetails: false,
                financialDetails: true,
              })
            }}
          >
            <i className='bi bi-pencil me-2'></i>
            {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
          </button>
        )}
      </div>
    </div>
  )
}
